import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, Users, DollarSign, Target, Award,
  Phone, MessageSquare, RefreshCw, AlertTriangle, CheckCircle,
  XCircle, Clock, BarChart3
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { leadsAPI } from '../../utils/api';
import { toast } from 'sonner';

const STAGE_LABELS = {
  new_lead: 'New Leads',
  in_discussion: 'In Discussion',
  prospective: 'Prospective',
  payment_received: 'Payment Received',
  registered: 'Registered',
};

const FUNNEL_COLORS = ['#6366f1', '#8b5cf6', '#f59e0b', '#10b981', '#14b8a6'];
const SOURCE_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#10b981', '#f43f5e', '#06b6d4', '#8b5cf6', '#84cc16', '#ec4899', '#f97316'];

const AGING_CONFIG = [
  { key: 'healthy', label: 'Healthy', sub: '≤ 3 days', color: 'text-emerald-700', bg: 'bg-emerald-50', bar: '#10b981', icon: CheckCircle },
  { key: 'at_risk', label: 'At Risk', sub: '4-7 days', color: 'text-amber-700', bg: 'bg-amber-50', bar: '#f59e0b', icon: Clock },
  { key: 'critical', label: 'Critical', sub: '7+ days', color: 'text-rose-700', bg: 'bg-rose-50', bar: '#f43f5e', icon: AlertTriangle },
];

function KPICard({ icon: Icon, label, value, sub, color, delta }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{label}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
        {delta != null && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${delta >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
            <TrendingUp className="w-3 h-3" />
            <span>{delta >= 0 ? '+' : ''}{delta}% vs last period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function FunnelViz({ funnel }) {
  if (!funnel?.length) return <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No data</div>;
  const max = funnel[0]?.count || 1;
  return (
    <div className="space-y-2">
      {funnel.map((stage, i) => {
        const pct = max > 0 ? (stage.count / max) * 100 : 0;
        return (
          <div key={stage.stage}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-600 font-medium">{STAGE_LABELS[stage.stage] || stage.stage}</span>
              <div className="flex items-center gap-3">
                <span className="font-bold text-slate-800">{stage.count}</span>
                {i > 0 && stage.drop_rate > 0 && (
                  <span className="text-rose-500">-{stage.drop_rate}%</span>
                )}
                <span className="text-slate-400">{stage.conversion_from_top}%</span>
              </div>
            </div>
            <div className="h-8 bg-slate-100 rounded-lg overflow-hidden relative">
              <div
                className="h-full rounded-lg transition-all duration-700 flex items-center justify-center"
                style={{ width: `${pct}%`, background: FUNNEL_COLORS[i] }}
              >
                {pct > 15 && <span className="text-white text-xs font-bold">{stage.count}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function SalesReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState(30);
  const [pipeline, setPipeline] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [trends, setTrends] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [sources, setSources] = useState([]);
  const [aging, setAging] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pipeRes, funnelRes, trendRes, consRes, srcRes, agingRes] = await Promise.allSettled([
        leadsAPI.getPipelineAnalytics(),
        leadsAPI.getFunnelAnalytics(),
        leadsAPI.getTrends(trendDays),
        leadsAPI.getConsultantPerformance(),
        leadsAPI.getSourceBreakdown(),
        leadsAPI.getAgingSummary(),
      ]);
      if (pipeRes.status === 'fulfilled') setPipeline(pipeRes.value.data);
      if (funnelRes.status === 'fulfilled') setFunnel(funnelRes.value.data.funnel || []);
      if (trendRes.status === 'fulfilled') setTrends(trendRes.value.data.trend || []);
      if (consRes.status === 'fulfilled') setConsultants(consRes.value.data.consultants || []);
      if (srcRes.status === 'fulfilled') setSources(srcRes.value.data.sources || []);
      if (agingRes.status === 'fulfilled') setAging(agingRes.value.data);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }, [trendDays]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const trendChartData = trends.map((t) => ({
    date: t.date.slice(5),
    'New Leads': t.created,
    Registered: t.registered,
  }));

  const consultantChartData = consultants.slice(0, 8).map((c) => ({
    name: c.name.split(' ')[0],
    Leads: c.total_leads,
    Converted: c.converted,
    'At Risk': c.at_risk_leads,
  }));

  const sc = pipeline?.stage_counts || {};
  const totalLeads = pipeline?.total_leads || 0;
  const agingTotal = aging ? aging.healthy + aging.at_risk + aging.critical : 0;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content-bare overflow-y-auto bg-slate-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Sales Reports</h1>
            <p className="text-sm text-slate-500">Pipeline analytics, consultant performance & SLA health</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array(8).fill(0).map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-5 h-24" /></Card>)}
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard icon={Users} label="Total Leads" value={totalLeads} color="bg-indigo-500" />
              <KPICard icon={CheckCircle} label="Registered" value={sc.registered || 0} sub="converted clients" color="bg-teal-500" />
              <KPICard icon={DollarSign} label="Pipeline Value" value={`INR${((pipeline?.pipeline_value || 0) / 1000).toFixed(0)}k`} color="bg-emerald-500" />
              <KPICard icon={TrendingUp} label="Conversion Rate" value={`${pipeline?.conversion_rate || 0}%`} color="bg-violet-500" />
            </div>

            {/* SLA Aging Summary */}
            {aging && (
              <div className="grid grid-cols-3 gap-4">
                {AGING_CONFIG.map(({ key, label, sub, color, bg, bar, icon: Icon }) => {
                  const val = aging[key] || 0;
                  const pct = agingTotal > 0 ? Math.round((val / agingTotal) * 100) : 0;
                  return (
                    <Card key={key} className={`border-l-4`} style={{ borderLeftColor: bar }}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`w-4 h-4 ${color}`} />
                          <span className={`text-sm font-semibold ${color}`}>{label}</span>
                          <span className="text-xs text-slate-400 ml-auto">{sub}</span>
                        </div>
                        <p className="text-3xl font-bold text-slate-900">{val}</p>
                        <p className="text-xs text-slate-500 mt-1">{pct}% of active leads</p>
                        <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: bar }} />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Charts row 1: Trend + Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Trend chart */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Lead Trends</CardTitle>
                    <div className="flex items-center gap-1">
                      {[7, 14, 30, 60].map((d) => (
                        <Button key={d} size="sm" variant={trendDays === d ? 'default' : 'ghost'}
                          className={`h-7 px-2.5 text-xs ${trendDays === d ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                          onClick={() => setTrendDays(d)}>
                          {d}d
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={trendChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorNew" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="New Leads" stroke="#6366f1" strokeWidth={2} fill="url(#colorNew)" dot={false} />
                      <Area type="monotone" dataKey="Registered" stroke="#14b8a6" strokeWidth={2} fill="url(#colorReg)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Funnel */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-indigo-500" /> Pipeline Funnel
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FunnelViz funnel={funnel} />
                </CardContent>
              </Card>
            </div>

            {/* Charts row 2: Consultant bar + Source pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Consultant performance bar */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" /> Consultant Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {consultantChartData.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No consultant data yet</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={consultantChartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Leads" fill="#e0e7ff" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Converted" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="At Risk" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Source breakdown donut */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Lead Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  {sources.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No source data</p>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={sources}
                            dataKey="count"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={3}
                            label={({ name, percent }) => percent > 0.05 ? `${Math.round(percent * 100)}%` : ''}
                            labelLine={false}
                          >
                            {sources.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [`${v} leads`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1.5 mt-2">
                        {sources.map((s, i) => {
                          const total = sources.reduce((a, b) => a + b.count, 0);
                          const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                          return (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                              <span className="text-slate-700 font-medium truncate flex-1">{s.name}</span>
                              <span className="font-bold text-slate-800">{s.count}</span>
                              <span className="text-slate-400 w-8 text-right">{pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Consultant detail table */}
            {consultants.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Consultant Detail</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          {['#', 'Consultant', 'Total', 'Active', 'Converted', 'Not Int.', 'Conv. %', 'Calls', 'Notes', 'At Risk', 'Pipeline'].map((h) => (
                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {consultants.map((c, i) => (
                          <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {(c.name || '?').slice(0, 2).toUpperCase()}
                                </div>
                                <span className="font-medium text-slate-800">{c.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-semibold text-slate-700">{c.total_leads}</td>
                            <td className="px-4 py-3 text-indigo-700">{c.active}</td>
                            <td className="px-4 py-3 text-teal-700 font-bold">{c.converted}</td>
                            <td className="px-4 py-3 text-slate-500">{c.not_interested}</td>
                            <td className="px-4 py-3">
                              <Badge className={`text-xs ${c.conversion_rate >= 20 ? 'bg-emerald-100 text-emerald-700' : c.conversion_rate >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {c.conversion_rate}%
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-600 flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" />{c.calls_made}
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-slate-400" />{c.notes_added}</span>
                            </td>
                            <td className="px-4 py-3">
                              {c.at_risk_leads > 0 ? (
                                <Badge className="bg-rose-100 text-rose-700 text-xs">{c.at_risk_leads} ⚠</Badge>
                              ) : (
                                <span className="text-emerald-500 text-xs">✓</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">
                              INR{(c.pipeline_value / 1000).toFixed(0)}k
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
